import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role = 'user', company_id = null) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role, company_id });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const isSystemAdmin = () => user?.role === 'system_admin';
  const isAdmin = () => ['system_admin', 'company_admin', 'admin'].includes(user?.role);
  const hasFeature = (feature) => {
    if (isAdmin()) return true;
    if (feature === 'actions') return true;
    const aliases = { audits: 'audits_view', traceability: 'traceability_view', documents: 'documents_view', quality: 'quality_view' };
    const resolved = aliases[feature] || feature;
    const access = user?.feature_access || {};
    if (access[resolved] === true) return true;
    return resolved.endsWith('_view') && access[resolved.replace('_view', '')] === true;
  };
  const isAuditCreator = () => hasFeature('audits_edit') && ['system_admin', 'company_admin', 'admin', 'audit_creator'].includes(user?.role);
  const canRunAudits = () => !!user && hasFeature('audits');
  const canViewReports = () => !!user && hasFeature('audits');

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      refreshUser: fetchUser,
      logout,
      isSystemAdmin,
      isAdmin,
      hasFeature,
      isAuditCreator,
      canRunAudits,
      canViewReports
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
